import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { isValidElement, useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  createReadableClass,
  invariant,
  isMobile
} from '@elfsight/widget-utils';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { usePortalNestProvider } from '../portal';
import { zIndexLayer } from '../zIndexLayer';

const ANIMATION_DURATION_MS = 200;
const DELAY = 0;
const SKIP_DELAY = 100;

export type TooltipProps = {
  children: ReactElement;
  title?: ReactNode;
  background?: string;
  fontColor?: string;
  borderRadius?: number;
  withoutTransition?: boolean;
  disabled?: boolean;
  maxWidth?: number;
  popoutZIndex?: number;
  style?: CSSProperties;
  offset?: number;
  withPortal?: boolean;
  container?: HTMLElement | null;
};

export function Tooltip({
  children,
  title,
  background = 'rgb(17, 17, 17)',
  fontColor = 'rgb(255, 255, 255)',
  borderRadius = 4,
  disabled = false,
  offset = 4,
  popoutZIndex = zIndexLayer.tooltip,
  withoutTransition = false,
  maxWidth = 300,
  style,
  withPortal = true,
  container: containerProp
}: TooltipProps) {
  invariant(
    isValidElement(children),
    'Expected the children to be a valid react element.'
  );

  const nest = usePortalNestProvider();
  const container = containerProp ?? nest ?? document.body;

  const [open, setOpen] = useState(false);
  const isMobileDevice = isMobile();

  // Radix Toolkit does not support mobile behavior, so we are handle it by yourself
  const triggerClickHandler = () => {
    if (isMobileDevice) {
      setOpen((prevState) => !prevState);
    }
  };

  const openChangeHandler = isMobileDevice ? undefined : setOpen;

  useEffect(() => {
    if (!open) return;
    const handleScroll = () => setOpen(false);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [open]);

  if (disabled) return children;

  return (
    <RadixTooltip.Provider delayDuration={DELAY} skipDelayDuration={SKIP_DELAY}>
      <RadixTooltip.Root open={open} onOpenChange={openChangeHandler}>
        <RadixTooltip.Trigger onClick={triggerClickHandler} asChild>
          {children}
        </RadixTooltip.Trigger>
        <TooltipPortal withPortal={withPortal} container={container}>
          <StyledContent
            onPointerDownOutside={() => setOpen(false)}
            side={'top' as const}
            align={'center' as const}
            sideOffset={offset}
            collisionPadding={8}
            $maxWidth={maxWidth}
            $background={background}
            $color={fontColor}
            $borderRadius={borderRadius}
            $zIndex={popoutZIndex}
            $withoutTransition={withoutTransition}
            style={style}
          >
            {title}
          </StyledContent>
        </TooltipPortal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

function TooltipPortal({
  withPortal,
  container,
  children
}: {
  withPortal: boolean;
  container: HTMLElement | null;
  children: ReactNode;
}) {
  if (!withPortal) return <>{children}</>;

  return (
    <RadixTooltip.Portal container={container}>{children}</RadixTooltip.Portal>
  );
}

const slideIn = keyframes`
  from { opacity: 0; translate: 0 4px; }
  to { opacity: 1; translate: 0 0; }
`;

const slideOut = keyframes`
  from { opacity: 1; translate: 0 0; }
  to { opacity: 0; translate: 0 4px; }
`;

const StyledContent = styled(RadixTooltip.Content).attrs({
  className: createReadableClass({ component: 'tooltip', element: 'container' })
})<{
  $maxWidth?: number;
  $background?: string;
  $color?: string;
  $borderRadius?: number;
  $zIndex?: number;
  $withoutTransition: boolean;
}>`
  ${({
    $maxWidth,
    $background,
    $color,
    $borderRadius,
    $zIndex,
    $withoutTransition
  }) => css`
    max-width: ${$maxWidth}px;
    background: ${$background};
    color: ${$color};
    border-radius: ${$borderRadius}px;
    padding: 6px 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    text-align: center;
    font-size: 13px;
    line-height: 1.33;
    z-index: ${$zIndex};
    will-change: opacity;

    animation-duration: ${$withoutTransition ? 0 : ANIMATION_DURATION_MS}ms;
    animation-timing-function: ease-out;

    &[data-state='delayed-open'],
    &[data-state='instant-open'] {
      animation-name: ${slideIn};
    }

    &[data-state='closed'] {
      animation-name: ${slideOut};
    }
  `}
`;
